import { useEffect, useRef } from 'react';
import type { ClientToHub, Command, HubToClient, RobotState } from '@fabricos/protocol-ts';
import { recordEvent } from '@/store/appEvents';
import { useAuth } from '@/store/auth';
import { useCommandLog } from '@/store/commandLog';
import { useConnection } from '@/store/connection';
import { useFleet } from '@/store/fleet';
import { useFlightStats } from '@/store/flightStats';
import { useParams } from '@/store/params';
import { useReplay } from '@/store/replay';
import { useSelection } from '@/store/selection';
import { eventSummary } from '@/lib/events';
import { HUB_WS_BASE, IS_TAURI } from '@/lib/hubUrl';
import { log } from '@/lib/logger';
import {
  loadRecentTrails,
  queueTrailPoints,
  setTrailBudgetProvider,
} from '@/lib/trailDb';
import { useSettings } from '@/store/settings';

/** Commands issued from the Assets workspace; everything else is Pilot. */
const ASSETS_KINDS = new Set([
  'param_read',
  'param_read_all',
  'param_set',
  'reboot',
  'motor_test',
  'sensor_calibration',
]);

/** High-rate stick streams — never worth an event entry each. */
const STREAMED_KINDS = new Set(['manual_control', 'velocity_setpoint']);

function robotName(robotId: string): string | undefined {
  return useFleet.getState().robots[robotId]?.name;
}

/**
 * State-frame coalescing. The Hub relays a state snapshot per decoded
 * MAVLink message (~18/s per drone). Applying each one individually means
 * a zustand notify + React re-render wave per frame — with several drones
 * that's what made the whole UI lag. Instead: keep only the latest frame
 * per robot and flush the batch every STATE_FLUSH_MS in ONE store update.
 * The map painter interpolates between fixes, so nothing looks slower.
 */
const STATE_FLUSH_MS = 100;
const pendingStates = new Map<string, RobotState>();
let stateFlushTimer: number | null = null;

function scheduleStateFlush() {
  if (stateFlushTimer !== null) return;
  stateFlushTimer = window.setTimeout(() => {
    stateFlushTimer = null;
    if (pendingStates.size === 0) return;
    const batch = Array.from(pendingStates.values());
    pendingStates.clear();
    useFleet.getState().applyStates(batch);
    queueTrailPoints(batch);
    const flightStats = useFlightStats.getState();
    for (const st of batch) flightStats.ingest(st.robot_id, st);
    const replay = useReplay.getState();
    for (const st of batch) replay.capture(st);
  }, STATE_FLUSH_MS);
}

// Persistence bootstrap: budget provider + one-time trail restore (last 24 h)
// so a refresh doesn't lose the breadcrumb history.
setTrailBudgetProvider(() => useSettings.getState().storage.trailBudgetMb);
let trailsHydrated = false;
function hydrateTrailsOnce(): void {
  if (trailsHydrated) return;
  trailsHydrated = true;
  loadRecentTrails(Date.now() - 24 * 3_600_000)
    .then((saved) => {
      if (Object.keys(saved).length > 0) useFleet.getState().hydrateTrails(saved);
    })
    .catch((err) => console.error('[trailDb] hydrate failed', err));
}

const INITIAL_RETRY_MS = 500;
const MAX_RETRY_MS = 8_000;

function wsUrl(): string {
  return `${HUB_WS_BASE}/ws`;
}

/**
 * Active transport. Modules outside the React tree (command senders, gamepad
 * poll loop) call `sendCommand` / `sendPing`; they need to reach the right
 * transport without holding a hook handle.
 */
type Transport = 'ws' | 'tauri';
let activeTransport: Transport | null = null;
let activeSocket: WebSocket | null = null;

/** Send a Command via whichever transport is currently up. */
export function sendCommand(command: Command): boolean {
  const msg: ClientToHub = { type: 'command', command };
  useCommandLog.getState().record(command.id, command.kind);
  if (!STREAMED_KINDS.has(command.kind)) {
    recordEvent(
      ASSETS_KINDS.has(command.kind) ? 'assets' : 'pilot',
      `sent ${command.kind.replace(/_/g, ' ')}`,
      { robotId: command.robot_id, robotName: robotName(command.robot_id) },
    );
  }
  if (activeTransport === 'tauri') {
    void tauriSend(msg);
    log.command(`${command.kind} → ${command.robot_id}`, {
      kind: command.kind,
      robot_id: command.robot_id,
      command,
    });
    return true;
  }
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
    log.warn('command', `dropped ${command.kind} — WS not open`, {
      kind: command.kind,
      robot_id: command.robot_id,
    });
    return false;
  }
  activeSocket.send(JSON.stringify(msg));
  log.command(`${command.kind} → ${command.robot_id}`, {
    kind: command.kind,
    robot_id: command.robot_id,
    command,
  });
  return true;
}

/** Send a Ping for RTT measurement. */
export function sendPing(): boolean {
  const msg: ClientToHub = { type: 'ping', ts_ms: BigInt(Date.now()) };
  if (activeTransport === 'tauri') {
    void tauriSend(msg);
    return true;
  }
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return false;
  activeSocket.send(JSON.stringify(msg));
  return true;
}

/**
 * Singleton transport bridge. On Tauri, uses `hub_send` / `hub:msg` event;
 * elsewhere, opens a WebSocket with exponential-backoff reconnect.
 */
export function useHubSocket() {
  const sock = useRef<WebSocket | null>(null);
  const retryMs = useRef<number>(INITIAL_RETRY_MS);
  const closed = useRef(false);

  const token = useAuth((s) => s.token);

  useEffect(() => {
    if (!token) return;
    hydrateTrailsOnce();

    if (IS_TAURI) {
      let unlisten: (() => void) | null = null;
      let cancelled = false;
      activeTransport = 'tauri';
      useConnection.getState().setStatus('connecting');

      (async () => {
        try {
          const [{ listen }, { invoke }] = await Promise.all([
            import('@tauri-apps/api/event'),
            import('@tauri-apps/api/core'),
          ]);

          const dispose = await listen<string>('hub:msg', (event) => {
            try {
              const msg = JSON.parse(event.payload) as HubToClient;
              dispatch(msg);
            } catch (e) {
              console.warn('hub:msg parse error', e);
            }
          });

          if (cancelled) {
            dispose();
            return;
          }
          unlisten = dispose;

          // Authenticate, then signal ready so Rust flushes welcome + roster.
          const hello: ClientToHub = {
            type: 'hello',
            protocol_version: '0.1.0',
            client: `token:${token}`,
          };
          await invoke('hub_send', { msg: JSON.stringify(hello) });
          await invoke('hub_ready');

          useConnection.getState().setStatus('connected');
          log.ws('tauri-events transport ready');
        } catch (e) {
          log.error('ws', `tauri transport init failed: ${e}`);
          useConnection.getState().setStatus('disconnected');
        }
      })();

      return () => {
        cancelled = true;
        unlisten?.();
        activeTransport = null;
        useConnection.getState().setStatus('disconnected');
      };
    }

    // ── Cloud web / dev: real WebSocket ────────────────────────────────
    closed.current = false;
    activeTransport = 'ws';
    connect();

    return () => {
      closed.current = true;
      sock.current?.close();
      sock.current = null;
      activeSocket = null;
      activeTransport = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function connect() {
    useConnection.getState().setStatus('connecting');
    const ws = new WebSocket(wsUrl());
    sock.current = ws;
    activeSocket = ws;

    ws.onopen = () => {
      retryMs.current = INITIAL_RETRY_MS;
      useConnection.getState().setStatus('connected');
      log.ws('ws connected');
      // Smuggle the bearer token through the Hello's `client` field for now.
      // Hub auth checks for prefix "token:". Protocol-level field is on the
      // backlog (see BACKLOG CQ-x).
      const tok = useAuth.getState().token;
      const hello: ClientToHub = {
        type: 'hello',
        protocol_version: '0.1.0',
        client: tok ? `token:${tok}` : 'web',
      };
      ws.send(JSON.stringify(hello));
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as HubToClient;
        dispatch(msg);
      } catch (e) {
        console.warn('ws parse error', e);
      }
    };

    ws.onclose = (ev) => {
      activeSocket = null;
      useConnection.getState().setStatus(closed.current ? 'disconnected' : 'reconnecting');
      log.ws(`ws closed (code ${ev.code})`, { code: ev.code, reason: ev.reason });
      if (!closed.current) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      /* handled by onclose */
    };
  }

  function scheduleReconnect() {
    const delay = retryMs.current;
    retryMs.current = Math.min(retryMs.current * 2, MAX_RETRY_MS);
    setTimeout(() => {
      if (!closed.current) connect();
    }, delay);
  }
}

/**
 * Send a ClientToHub message via the Tauri `hub_send` command. BigInt fields
 * (e.g. `ts_ms` on Ping) need a custom replacer because JSON.stringify won't
 * serialise bigints natively.
 */
async function tauriSend(msg: ClientToHub): Promise<void> {
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const json = JSON.stringify(msg, (_k, v) =>
      typeof v === 'bigint' ? Number(v) : v,
    );
    await invoke('hub_send', { msg: json });
  } catch (e) {
    log.error('command', `tauri hub_send failed: ${e}`);
  }
}

function dispatch(msg: HubToClient) {
  const fleet = useFleet.getState();
  const conn = useConnection.getState();

  switch (msg.type) {
    case 'welcome':
      conn.setSession(msg.session_id);
      log.ws(`welcome · session ${msg.session_id.slice(0, 8)}`);
      break;
    case 'robots':
      fleet.setRobots(msg.robots);
      useSelection.getState().pruneTo(msg.robots.map((r) => r.id));
      log.ws(`roster · ${msg.robots.length} robot(s)`, {
        ids: msg.robots.map((r) => r.id),
      });
      break;
    case 'robot_added':
      fleet.addRobot(msg.robot);
      log.ws(`robot added · ${msg.robot.id}`, { robot: msg.robot });
      break;
    case 'robot_removed':
      fleet.removeRobot(msg.robot_id);
      useSelection.getState().deselect(msg.robot_id);
      log.ws(`robot removed · ${msg.robot_id}`);
      break;
    case 'state':
      // Coalesced — see scheduleStateFlush. High-volume, silent on success.
      pendingStates.set(msg.state.robot_id, msg.state);
      scheduleStateFlush();
      break;
    case 'event':
      fleet.pushEvent(msg.event);
      // Side-channel: param table streams in as RobotEvent::ParamValue;
      // fan it into the params store so the Assets browser can render
      // without re-walking every fleet event.
      if (msg.event.kind === 'param_value') {
        const e = msg.event as Extract<typeof msg.event, { kind: 'param_value' }>;
        useParams.getState().upsertParam(e.robot_id, {
          id: e.param_id,
          value: e.value,
          param_type: e.param_type,
          index: e.index,
          count: e.count,
          updated_at_ms: Number(e.ts_ms),
        });
      }
      // Robot-side happenings land in the app event register under Pilot
      // (the flight module owns the vehicles), tagged with the robot name.
      if (msg.event.kind !== 'param_value') {
        recordEvent('pilot', eventSummary(msg.event), {
          severity:
            msg.event.severity === 'critical'
              ? 'critical'
              : msg.event.severity === 'warning'
                ? 'warning'
                : 'info',
          robotId: msg.event.robot_id,
          robotName: robotName(msg.event.robot_id),
        });
      }
      log.ws(`event · ${msg.event.kind}`, { event: msg.event });
      break;
    case 'ack':
      fleet.pushAck(msg.ack);
      log.ws(`ack · ${msg.ack.status}`, { ack: msg.ack });
      break;
    case 'pong':
      break;
    case 'error':
      log.error('ws', 'hub error', { msg });
      break;
  }
}
